import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { formatNumber, i18nIntl } from '../../../lib/ep';
import { activate_pack_trial, get_credit } from '../../../lib/stage_ocr_ticket';

import ActionButton, { MainButton, SquaredButton } from '../../builder/ActionButton';
import ActionLink from '../../builder/ActionLink';
import ButtonCollection from '../../builder/ButtonCollection';
import Message, { AlertMessage } from '../../builder/Message';
import ProgressBar from '../../builder/ProgressBar';
import Spinner from '../../builder/Spinner';
import Tooltip from '../../builder/Tooltip';

import { ProfileModule } from '../../ui_items/Module';
import PackSelectionLayer from '../PackSelectionLayer';
import FloatingLayer from '../../builder/FloatingLayer';
import SaveButton from '../../builder/SaveButton';
import SectionTitle from '../../builder/SectionTitle';
import SectionSubtitle from '../../builder/SectionSubtitle';

export const PurchaseLayer = (props) => {
	return (
		<PackSelectionLayer
			title="AI-Validation packs purchase"
			pack_type={2}
			avail_units={[500, 1000, 5000, 10000, 50000, 100000, 500000, 1000000]}
			unit_label={{
				singular:"AI-Validation",
				plural:"AI-Validations",
			}}
			conditions={[
				"AI-Validations purchased and not consumed do not expire.",
				"The AI-Validation credits accumulate in your account, and you can use them for any of your promotions.",
			]}
			{...props}
		/>
	)
};

const Wrapper = (props) => {
	const is_builder = props?.is_builder ?? false;
	const add_module = props?.add_module ?? false;

	if(is_builder && !add_module) {
		return (
			<div
				class={`gui_pos_rel ${props?.classes ?? ''}`}
				style={props.style}
			>{props.children}</div>
		)
	}

	return (
		<ProfileModule
			title={{
				label:"AI-powered receipt validations",
				icon:'knk-package',
			}}
			is_gapped={false}
			style={props.style}
			_class={{ other:props.classes }}
		>{props.children}</ProfileModule>
	)
};

const OCRPacksStatus = (props) => {
	const i18n = (m) => i18nIntl(m, props.intl);

	const [ data, setData ] = useState({ total:100, used:1, initial:true });
	const [ is_processing, setIsProcessing ] = useState(false);
	const [ display_purchase_layer, setDisplayPurchaseLayer ] = useState(false);
	const [ display_trial_confirm_layer, setDisplayTrialConfirmLayer ] = useState(false);

	const is_builder = props?.is_builder ?? false;
	const add_module = props?.add_module ?? false;

	const updateCredit = () => {
		get_credit(2, setData);
	};

	const executeDelayedAction = (cb) => {
		setIsProcessing(true);

		setTimeout(() => {
			cb();
			setIsProcessing(false);
		}, (Math.round(Math.random() * 1000) + 1000));
	};

	useEffect(() => {
		updateCredit();
	}, []);

	const activateTrial = (e, cb) => {
		executeDelayedAction(() => activate_pack_trial(2, setData));
		cb();
		setDisplayTrialConfirmLayer(false);
	};

	const warning_percent = 25;
	const total = parseInt(data.total);
	const pending = total - parseInt(data.used);
	const percent = formatNumber(100 * pending / total, 1);
	const almost_finished = (percent <= warning_percent) ? true : false;
	const still_available = (pending > 0) ? true : false;

	let classes = ['ep_pack_status'];
	if(!is_builder || !add_module) classes.push('not_in_top');
	if(!still_available) classes.push('is_finished');
	if(almost_finished) classes.push('almost_finished');

	const CTA_component = (!is_builder) ? ActionButton : ActionLink;

	if(is_processing) {
		const block_h = '100px';

		return (
			<Wrapper
				classes={classes.join(" ")}
				is_builder={is_builder}
				add_module={add_module}
				style={{
					height:block_h,
					outline:'1px dashed rgba(var(--main-text-color), 0.15)',
					outlineOffset:'0',
				}}
			>
				<Spinner
					style={{ height:block_h }}
				/>
			</Wrapper>
		);
	}

	const trial_available = (data.pending_trial != undefined) ? true : false;

	if(trial_available) {
		return (
			<Wrapper
				classes={classes.join(" ")}
				is_builder={is_builder}
				add_module={add_module}
			>
				<Message
					title={`${i18n("Free trial")}: 50 ${i18n("AI-Validations")}`}
					_class={{ other:(is_builder && add_module) ? 'in_bottom' : 'in_top' }}
				>
					<p>{i18n("To help you discover how AI-powered receipt validation works, we're giving you 50 free validations. Use them to test the system with no commitment.")}</p>

					<p>{props.intl.formatMessage({ id:"You need an {type} to analyze and validate the purchase receipts uploaded by users. Packs are priced based on the number of validations." }, { type:i18n("AI-Validation Pack") })}</p>

					<p>
						<ActionLink
							onClick={() => setDisplayTrialConfirmLayer(true)}
						>Activate 50 Free AI-Validations</ActionLink>
					</p>

					<p>
						<ActionLink
							onClick={() => setDisplayPurchaseLayer(true)}
						>View available packs</ActionLink>
					</p>
				</Message>

				{(display_purchase_layer) && (
					<PurchaseLayer
						close={() => setDisplayPurchaseLayer(false)}
					/>
				)}

				{(display_trial_confirm_layer) && (
					<FloatingLayer
						_class={{ size:'small' }}
						backButton={() => setDisplayTrialConfirmLayer(false)}
					>
						<div>
							<SectionTitle>Activation of the 50 AI Validation Trial</SectionTitle>

							<SectionSubtitle>The Purchase receipt (AI-powered validation) system uses OCR technology and artificial intelligence to detect information in receipts and prevent fraud. Automatic detection may be affected by image quality, receipt format, or advanced manipulation attempts. The administrator is responsible for reviewing and adjusting validation rules as needed.</SectionSubtitle>

							<SectionSubtitle>To evaluate the system's efficiency, we offer you 50 free validations.</SectionSubtitle>

							<ButtonCollection
								in_floating={true}
							>
								<SaveButton
									onClick={activateTrial}
								>Activate trial</SaveButton>
							</ButtonCollection>
						</div>
					</FloatingLayer>
				)}
			</Wrapper>
		)
	}

	return (
		<Wrapper
			classes={classes.join(" ")}
			is_builder={is_builder}
			add_module={add_module}
		>
			{/*<Message
				_class={{ other:(is_builder && add_module) ? 'in_bottom' : 'in_top' }}
			>
				<p>
					{i18n("You need an AI-Validation Pack to analyze and validate the purchase receipts uploaded by users. Packs are priced based on the number of validations.")}

					<ActionLink
						_href={`${window.zendesk_base_url}34968848348308`}
						_target='_blank'
						_class={{ other:'gui_not_in_start_tiny' }}
					>More information</ActionLink>
				</p>
			</Message>*/}

			<div class={`pack_progress${(is_builder) ? ' gui_not_in_top_small' : ''}`}>
				{(data?.initial ?? false) ? (
					<></>
				) : (
					<FormattedMessage
						id="AI-Validations in your account: {pending} out of {total} ({percent}%)"
						values={{
							pending:<strong>{pending}</strong>,
							total:<strong>{total}</strong>,
							percent,
						}}
					/>
				)}

				{(still_available) && (
					<ProgressBar
						actual={pending}
						max={total}
						display_percent={false}
						display_percent_in_bar={false}
						size="big"
						reinforcement_color='var(--ep-red-color)'
						animation_duration={`${0.05 * (100 - percent)}s`}
						extra_contents={<SquaredButton
							icon='knk-reload'
							onClick={() => executeDelayedAction(() => updateCredit())}
							tooltip={<Tooltip
								with_icon={false}
								position='top'
							>Refresh</Tooltip>}
							_class={{ other:'reload_bar' }}
						/>}
					/>
				)}
			</div>

			{(!still_available) && (
				<AlertMessage
					title="OCR validation disabled"
					_class={{ other:'in_top in_bottom' }}
				/>
			)}

			<ButtonCollection
				_class={{ other:'gui_flex_start' }}
				style={{
					gap:'1em',
				}}
			>
				<span>
					[ <CTA_component
						secondary={(almost_finished) ? false : true}
						onClick={() => setDisplayPurchaseLayer(true)}
					>Purchase an AI-Validation pack</CTA_component> ]
				</span>
			</ButtonCollection>

			{(display_purchase_layer) && (
				<PurchaseLayer
					close={() => setDisplayPurchaseLayer(false)}
				/>
			)}
		</Wrapper>
	);
};

export default injectIntl(OCRPacksStatus);